import { FC, useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { shallowEqual } from 'react-redux'

import { Box, Button, Dialog, DialogContent, Link, MenuItem, Skeleton, Stack, TableContainer, TextField, Typography } from '@mui/material'

import ConfirmDialog from '../../../components/ConfirmDialog/ConfirmDialog'
import GRDialogTitle from '../../../components/GRDialogTitle/GRDialogTitle'
import { GRTable, GRTableColumn } from '../../../components/GRTable/GRTable'
import VersionTagsList from '../../../components/VersionTagsList/VersionTagsList'
import { displaySnackBar } from '../../../features/snackbar'
import { useAppDispatch, useAppSelector } from '../../../hooks/storeHooks'
import languageService from '../../../services/LanguageService'
import { ConfirmDialogData } from '../../../types/ConfirmDialogData'
import { VersionTag } from '../../../types/VersionTag'
import { selectCachedGameVersionsByMarket, useGetGameVersionsQuery, useSaveVersionTagsMutation } from '../../api/game-analyses'
import { useGetInternalMarketsQuery } from '../../api/internal-markets'
import {
  getReleaseNotes,
  getVersionTagsBody,
  getVersionTagsSaveImpact,
  haveSameTags,
  needsVersionTagsSaveConfirmation,
  toggleVersionTag,
} from '../../features/games/helpers/versionTagsHelpers'
import { GameVersion } from '../../features/games/types/GameAnalyses'

const defaultMarketIso = 'us'

type VersionTagsDialogProps = {
  open: boolean
  appId?: number | null
  gameName: string
  marketIso?: string
  onClose: () => void
}

type CustomProps = {
  versionTags: VersionTag[]
  editedTags: Record<string, string[]>
  shownNotes: Record<string, boolean>
  savingVersions: Record<string, boolean>
  onToggleNotes: (version: string) => void
  onToggleTag: (version: GameVersion, tagId: string) => void
  onSave: (version: GameVersion) => void
}

export const VersionTagsDialog: FC<VersionTagsDialogProps> = ({ open, appId, gameName, marketIso = defaultMarketIso, onClose }) => {
  const { t } = useTranslation()
  const dispatch = useAppDispatch()
  const [activeMarketIso, setActiveMarketIso] = useState(marketIso)
  // Versions whose tags were toggled, also when toggled back, as legacy allows saving them unchanged.
  const [editedTags, setEditedTags] = useState<Record<string, string[]>>({})
  const [shownNotes, setShownNotes] = useState<Record<string, boolean>>({})
  const [savingVersions, setSavingVersions] = useState<Record<string, boolean>>({})
  const [confirmDialogData, setConfirmDialogData] = useState<ConfirmDialogData<GameVersion>>()
  const [discardDialogData, setDiscardDialogData] = useState<ConfirmDialogData<string>>()

  useEffect(() => {
    if (!open) return
    setActiveMarketIso(marketIso)
    setEditedTags({})
    setShownNotes({})
    setSavingVersions({})
    setConfirmDialogData(undefined)
    setDiscardDialogData(undefined)
  }, [open, marketIso, appId])

  const versionTags = useMemo(() => languageService.getVersionTags(), [])
  const marketsQuery = useGetInternalMarketsQuery(undefined, { skip: !open })
  const versionsQuery = useGetGameVersionsQuery({ appId: appId ?? 0, marketIso: activeMarketIso }, { skip: !open || !appId, refetchOnMountOrArgChange: true })
  const [saveVersionTags] = useSaveVersionTagsMutation()
  const loadedVersionsByMarket = useAppSelector((state) => selectCachedGameVersionsByMarket(state, appId), shallowEqual)

  const markets = useMemo(() => [...(marketsQuery.data ?? [])].sort((a, b) => a.name.localeCompare(b.name)), [marketsQuery.data])
  const versions = useMemo(() => versionsQuery.currentData ?? [], [versionsQuery.currentData])

  const showMessage = useCallback((message: string, severity: 'success' | 'error') => dispatch(displaySnackBar({ message, severity, open: true })), [dispatch])

  const handleSave = useCallback(
    async (version: GameVersion) => {
      if (!appId) return
      const name = version.version
      const tags = getVersionTagsBody(editedTags[name] ?? version.tags ?? [], versionTags)
      setSavingVersions((current) => ({ ...current, [name]: true }))
      try {
        const stored = await saveVersionTags({ appId, version: name, tags, marketIso: activeMarketIso }).unwrap()
        if (!stored || !haveSameTags(stored.tags, tags)) {
          showMessage(t('internal-analysts:games_version_tags_not_stored', { version: name }), 'error')
          return
        }
        // Tags toggled again while saving stay edited.
        setEditedTags((current) => {
          if (!current[name] || !haveSameTags(current[name], tags)) return current
          const { [name]: _saved, ...rest } = current
          return rest
        })
        showMessage(t('internal-analysts:games_version_tags_saved', { name: gameName, version: name }), 'success')
      } catch {
        showMessage(t('internal-analysts:games_version_tags_save_failed', { version: name }), 'error')
      } finally {
        setSavingVersions(({ [name]: _saving, ...rest }) => rest)
      }
    },
    [activeMarketIso, appId, editedTags, gameName, saveVersionTags, showMessage, t, versionTags]
  )

  const requestSave = useCallback(
    (version: GameVersion) => {
      const impact = getVersionTagsSaveImpact(version, activeMarketIso, loadedVersionsByMarket)
      if (!needsVersionTagsSaveConfirmation(impact)) return handleSave(version)
      const marketName = (iso: string) => markets.find((market) => market.iso === iso)?.name ?? iso.toUpperCase()
      setConfirmDialogData({
        title: t('internal-analysts:games_version_tags_confirm_title'),
        confirmText: (
          <Stack spacing={1}>
            <Typography variant="body2">{t('internal-analysts:games_version_tags_confirm', { version: version.version })}</Typography>
            {impact.selectedIsAutomatic && (
              <Typography variant="body2">{t('internal-analysts:games_version_tags_confirm_automatic', { market: marketName(activeMarketIso) })}</Typography>
            )}
            {impact.differingMarketIsos.length > 0 && (
              <Typography variant="body2">
                {t('internal-analysts:games_version_tags_confirm_differing', { markets: impact.differingMarketIsos.map(marketName).join(', ') })}
              </Typography>
            )}
          </Stack>
        ),
        actionText: t('internal-analysts:games_version_tags_confirm_action'),
        destructiveAction: true,
        data: version,
      })
    },
    [activeMarketIso, handleSave, loadedVersionsByMarket, markets, t]
  )

  const handleConfirm = (confirmed?: ConfirmDialogData<GameVersion>) => {
    setConfirmDialogData(undefined)
    if (confirmed?.data) handleSave(confirmed.data)
  }

  // Edits are kept per version name, which other markets share, so they can't follow the market.
  const switchMarket = (iso: string) => {
    setActiveMarketIso(iso)
    setEditedTags({})
  }

  const requestMarketChange = (iso: string) => {
    if (iso === activeMarketIso) return
    if (!Object.keys(editedTags).length) return switchMarket(iso)
    setDiscardDialogData({
      title: t('internal-analysts:games_version_tags_discard_title'),
      confirmText: t('internal-analysts:games_version_tags_discard_confirm'),
      actionText: t('internal-analysts:games_version_tags_discard_action'),
      destructiveAction: true,
      data: iso,
    })
  }

  const handleDiscardConfirm = (confirmed?: ConfirmDialogData<string>) => {
    setDiscardDialogData(undefined)
    if (confirmed?.data) switchMarket(confirmed.data)
  }

  const customProps = useMemo<CustomProps>(
    () => ({
      versionTags,
      editedTags,
      shownNotes,
      savingVersions,
      onToggleNotes: (version) => setShownNotes((current) => ({ ...current, [version]: !current[version] })),
      onToggleTag: (version, tagId) =>
        setEditedTags((current) => ({ ...current, [version.version]: toggleVersionTag(current[version.version] ?? version.tags ?? [], tagId) })),
      onSave: requestSave,
    }),
    [editedTags, requestSave, savingVersions, shownNotes, versionTags]
  )

  const tableColumns: GRTableColumn<GameVersion, CustomProps>[] = useMemo(
    () => [
      {
        labelAccessor: t('internal-analysts:games_analyses_version'),
        accessor: ({ row }) => row.version,
        cellProps: { sx: { verticalAlign: 'top' } },
      },
      {
        labelAccessor: t('internal-analysts:games_version_tags_release_notes'),
        accessor: ({ row, customTableProps }) => {
          const isShown = !!customTableProps?.shownNotes[row.version]
          return (
            <Stack alignItems="flex-start" spacing={1}>
              <Link component="button" variant="body2" onClick={() => customTableProps?.onToggleNotes(row.version)}>
                {t(isShown ? 'internal-analysts:games_version_tags_hide_notes' : 'internal-analysts:games_version_tags_show_notes')}
              </Link>
              {isShown && (
                <Typography variant="body2" component="div" sx={{ whiteSpace: 'pre-wrap', textAlign: 'left' }}>
                  {getReleaseNotes(row)}
                </Typography>
              )}
            </Stack>
          )
        },
        cellProps: { align: 'left', sx: { verticalAlign: 'top' } },
        headerCellProps: { align: 'left', sx: { width: '45%' } },
      },
      {
        labelAccessor: t('internal-analysts:games_version_tags_tags'),
        accessor: ({ row, customTableProps }) => {
          const tags = customTableProps?.versionTags ?? []
          const active = customTableProps?.editedTags[row.version] ?? row.tags ?? []
          return (
            <Box display="flex" flexWrap="wrap" gap={0.5} justifyContent="center" data-testid={`version-tags-${row.version}`}>
              <VersionTagsList
                versionTags={tags}
                selectedVersionTags={tags.filter(({ id }) => active.includes(id))}
                onSelect={(tag) => customTableProps?.onToggleTag(row, tag.id)}
                size="small"
              />
            </Box>
          )
        },
        cellProps: { sx: { verticalAlign: 'top' } },
      },
      {
        labelAccessor: '',
        accessor: ({ row, customTableProps }) => {
          const isSaving = !!customTableProps?.savingVersions[row.version]
          const isEdited = !!customTableProps?.editedTags[row.version]
          return (
            <Stack alignItems="flex-end" spacing={0.5}>
              <Button size="small" variant="contained" disabled={isSaving || !isEdited} onClick={() => customTableProps?.onSave(row)}>
                {t(isSaving ? 'internal-analysts:games_version_tags_saving' : 'internal-analysts:games_version_tags_save')}
              </Button>
              {isEdited && (
                <Typography variant="caption" color="warning.main" sx={{ textAlign: 'right' }}>
                  {t('internal-analysts:games_version_tags_save_scope')}
                </Typography>
              )}
            </Stack>
          )
        },
        cellProps: { align: 'right', sx: { verticalAlign: 'top' } },
        headerCellProps: { sx: { width: 200, minWidth: 200 } },
      },
    ],
    [t]
  )
  const [columns, setColumns] = useState(tableColumns)

  const renderVersions = () => {
    if (versionsQuery.isFetching && !versionsQuery.currentData) return <Skeleton variant="rectangular" height={240} />
    if (versionsQuery.isError) return <Typography color="error.main">{t('internal-analysts:games_version_tags_load_failed')}</Typography>
    if (!versions.length) return <Typography color="text.secondary">{t('internal-analysts:games_version_tags_none')}</Typography>
    return (
      <TableContainer>
        <GRTable
          rows={versions}
          columns={columns}
          onColumnsUpdated={setColumns}
          rowIdKey={(row, index) => `${row.version}-${index}`}
          customProps={customProps}
          striped
        />
      </TableContainer>
    )
  }

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="lg">
      <GRDialogTitle onClose={onClose}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2} flex={1} minWidth={0}>
          <span>{t('internal-analysts:games_version_tags_title', { name: gameName, market: activeMarketIso.toUpperCase() })}</span>
          <TextField
            select
            size="small"
            label={t('internal-common:market')}
            value={activeMarketIso}
            onChange={(event) => requestMarketChange(event.target.value)}
            sx={{ minWidth: 220 }}
          >
            {markets.map((market) => (
              <MenuItem key={market.id} value={market.iso}>
                {market.name}
              </MenuItem>
            ))}
            {!markets.some(({ iso }) => iso === activeMarketIso) && <MenuItem value={activeMarketIso}>{activeMarketIso}</MenuItem>}
          </TextField>
        </Stack>
      </GRDialogTitle>
      <DialogContent>{renderVersions()}</DialogContent>
      <ConfirmDialog open={!!confirmDialogData} confirmDialogData={confirmDialogData} onClose={handleConfirm} />
      <ConfirmDialog open={!!discardDialogData} confirmDialogData={discardDialogData} onClose={handleDiscardConfirm} />
    </Dialog>
  )
}
